import { CloseDialogFunc } from "src/components/common/dialogs/GenericDialogContext.ts";
import { DataDirtyStateChangeHandler } from "src/utils/dataDirtyStateChangeHandler.ts";
import {
	resolveComponentTreePropertyValues,
	resolvePartConfiguratorTreeResultValues,
} from "src/components/views/erp/configurator/configuratorForm/configuratorTree/partConfiguratorTreeResultValues.ts";
import { PartConfiguratorTopLevelView } from "src/components/views/erp/configurator/configuratorForm/PartConfiguratorTopLevelView.tsx";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import i18n from "i18next";
import { PartConfiguratorType } from "src/api/generated/erp/configurator/configuratorType/partConfiguratorType.ts";
import { PartConfiguratorTreeResult } from "src/components/views/erp/configurator/configuratorForm/configuratorTree/PartConfiguratorTreeStatefulView.tsx";
import { SitePartTestConfiguratorResultsView } from "./SitePartTestConfiguratorResultsView";
import { TestConfigurationPrintoutButton } from "src/components/views/erp/configurator/configuratorTesting/TestConfigurationPrintoutButton.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface SitePartTestConfiguratorViewProps {
	sitePartRevisionId: number;
	closeDialog?: CloseDialogFunc;
	onDataDirtyStateChanged?: DataDirtyStateChangeHandler;
}

export const SitePartTestConfiguratorView = ({
	sitePartRevisionId,
	closeDialog,
	onDataDirtyStateChanged,
}: SitePartTestConfiguratorViewProps) => {
	const { openDialog } = useGenericDialog();

	const configuratorType: PartConfiguratorType = {
		type: "sitePartTest",
		sitePartRevisionId: sitePartRevisionId,
	};

	return (
		<PartConfiguratorTopLevelView
			configuratorType={configuratorType}
			onDataDirtyStateChanged={onDataDirtyStateChanged}
			onCompleted={async (result) => {
				if (result.type === "success") {
					await onSubmit(result.value);
				} else {
					await closeDialog?.({ confirmIfEdited: result.isEdited });
				}
			}}
			propertyModelFooterExtraComponents={({ resolvePropertyValues }) => (
				<TestConfigurationPrintoutButton
					configuratorType={configuratorType}
					resolvePropertyValues={resolvePropertyValues}
				/>
			)}
			footerExtraComponents={({ rootConfiguratorItem }) => (
				<TestConfigurationPrintoutButton
					configuratorType={configuratorType}
					resolvePropertyValues={() => resolveComponentTreePropertyValues(rootConfiguratorItem)}
				/>
			)}
		/>
	);

	async function onSubmit(result: PartConfiguratorTreeResult) {
		const { configurationSessionId } = result;
		const propertyValues = await resolvePartConfiguratorTreeResultValues(configuratorType, result);
		openDialog(() => ({
			title: i18n.t("test_configuration"),
			agentContext: {
				key: defineAgentUiContextKey("erp.configurator.sitePartTestConfigurationResults.dialog"),
				data: {
					sitePartRevisionId,
					configurationSessionId,
				},
				pathSegments: [i18n.t("test_configuration")],
			},
			content: (
				<SitePartTestConfiguratorResultsView
					configurationSessionId={configurationSessionId}
					configuratorType={configuratorType}
					propertyValues={propertyValues}
				/>
			),
		}));
	}
};
